import { ConfigService } from "../../common/services/config/config.service";
import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, SimpleChanges, HostBinding, OnDestroy } from "@angular/core";
import { ChatBotStyleAndConfig } from "../../components/modals/style-and-config/style-config";
import { ActivatedRoute, Router } from "@angular/router";
import { UrlParamsService } from "../services/url-params/url-params.service";
import { SessionService } from "../services/app-state-services/session/session.service";
import { IframeModeService } from "../services/iframe-mode/iframe-mode.service";
import { IframeMode } from "../../enums/iframe-mode.enum";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-chat-main",
  templateUrl: "./chat-main.component.html",
  styleUrl: "./chat-main.component.scss",
  standalone: false,

})
export class ChatMainComponent implements OnDestroy {
  @Input() tenantCode : string | undefined = "";
  @Input() workshopId: string | undefined = "";
  @Output() closeModalEvent = new EventEmitter<boolean>();
  @HostBinding('class.iframe-mode') get isInIframeMode() { return this.iframeMode !== IframeMode.NONE; }
  
  showModal : boolean = false;
  isClosing: boolean = false;
  chatBotStyles!: ChatBotStyleAndConfig;
  urlParams: any = {};
  isPhoneNumberSession: boolean = false;
  isAppLoading: boolean = true;
  iframeMode: IframeMode = IframeMode.NONE;
  IframeMode = IframeMode;

  get isStandaloneFullScreenMode(): boolean {
    return environment.fullScreenMode && this.iframeMode === IframeMode.NONE;
  }
  
  private loaderTimeout?: number;
  private closeTimeout?: number;
  private configLoaded: boolean = false;

  constructor(
    private _configService: ConfigService, 
    private el: ElementRef, 
    private renderer: Renderer2, 
    private route: ActivatedRoute, 
    private router: Router, 
    private _urlParamsService: UrlParamsService,
    private _sessionService: SessionService,
    private elRef: ElementRef,
    private _iframeModeService: IframeModeService
  ) {
    this.iframeMode = this._iframeModeService.getIframeMode();
    
    if (this.iframeMode !== IframeMode.NONE) {
      this.isAppLoading = false;
    }
  }

  ngOnInit() {
    this.setupIframeMode();
    this.captureUrlParameters();
    this.handleTenantCode();

    if (this.iframeMode === IframeMode.INLINE) {
      this.openChatModal();
    }

    const shouldAutoOpen = this.isStandaloneFullScreenMode ||
      (this.isPhoneNumberSession && this.iframeMode !== IframeMode.WIDGET);
    if (shouldAutoOpen) {
      this.openChatModal();
    }
    
    this._configService.loadConfig();
    
    this._configService.chatBotStyleAndConfig$.subscribe((config) => {
      this.chatBotStyles = config;
      if (config && Object.keys(config).length > 0) {
        this.configLoaded = true;
        this.handleConfigLoaded(config);
      }
    });
    
    this.initializeLoader();
  }

  ngOnDestroy() {
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
  }

  private initializeLoader(): void {
    if (this.iframeMode !== IframeMode.NONE) {
      this.loaderTimeout = window.setTimeout(() => {
        if (!this.configLoaded && this.isAppLoading) {
          console.warn('Config load timeout - hiding loader after 5 seconds');
          this.hideLoader();
        }
      }, 5000);
    } else {
      this.isAppLoading = false;
    }
  }

  private handleConfigLoaded(config: ChatBotStyleAndConfig): void {
    this.configLoaded = true;
    
    if (this.iframeMode !== IframeMode.NONE) {
      if (this.showModal && this.isAppLoading) {
        this.hideLoader();
      }
    }
  }

  private hideLoader(): void {
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = undefined;
    }
    
    setTimeout(() => {
      this.isAppLoading = false;
    }, 300);
  }

  private setupIframeMode(): void {
    if (this.iframeMode !== IframeMode.NONE) {
      window.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'TITAN_CHATBOT') {
          this.handleParentMessage(event.data.action);
        }
      });

      setTimeout(() => {
        this._iframeModeService.sendMessageToParent('IFRAME_READY', {
          status: 'loaded',
          mode: this.iframeMode
        });
      }, 500);
    }
  }

  private handleParentMessage(action: string): void {
    switch (action) {
      case 'OPEN':
        this.openChatModal();
        break;
      case 'CLOSE':
        this.closeChatModal();
        break;
      case 'TOGGLE':
        this.openChatModal();
        break;
    }
  }

  private captureUrlParameters() {
    const storedParams = this._urlParamsService.getStoredParams();
    if (storedParams) {
      this.urlParams = storedParams;
      this._urlParamsService.clearStoredParams();
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      
      this.urlParams = {
        phone_number: urlParams.get('phone_number'),
        service: urlParams.get('service')
      };
    }

    this.isPhoneNumberSession = !!this.urlParams.phone_number;
  }

  private handleTenantCode() {
    if (this.iframeMode !== IframeMode.NONE) {
      return;
    }

    if (!this.tenantCode) {
      const urlParams = new URLSearchParams(window.location.search);
      const nativeEl = this.elRef.nativeElement as HTMLElement;

      const tenantCodeFromUrl = urlParams.get('tenantCode');

      if (tenantCodeFromUrl) {
        this.tenantCode = tenantCodeFromUrl;
      } else {
        this.tenantCode =
          nativeEl.getAttribute('data-tenant-code') ||
          undefined;
      }

      this._sessionService.setTenantCode(this.tenantCode || "");
    }

    if (!this.workshopId) {
      const urlParams = new URLSearchParams(window.location.search);
      const nativeEl = this.elRef.nativeElement as HTMLElement;

      const workshopIdFromUrl = urlParams.get('workshopId');
      if (workshopIdFromUrl) {
        this.workshopId = workshopIdFromUrl;
      } else {
        this.workshopId = nativeEl.getAttribute('data-workshop-id') || undefined;
      }

      if (this.workshopId) {
        this._sessionService.setWorkshopId(this.workshopId);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["tenantCode"] && changes["tenantCode"].currentValue) {
      this._sessionService.setTenantCode(changes["tenantCode"].currentValue);
    }
    if (changes["workshopId"] && changes["workshopId"].currentValue) {
      this._sessionService.setWorkshopId(changes["workshopId"].currentValue);
    }
  }

  openChatModal() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = undefined;
    }
    this.showModal = true;
    this.isClosing = false;
    
    if (this.iframeMode !== IframeMode.NONE) {
      this._iframeModeService.sendMessageToParent('OPENED', { status: 'open' });
    }
  }

  playMessageDrop() {
    setTimeout(() => {
      const audio = new Audio(this._sessionService.getBaseAssetUrl() + "assets/sounds/message_drop.mp3");
      audio.play().catch((error) => {
        console.error("Error playing beep sound:", error);
      });
    }, 0);
  }

  closeChatModal() {
    this.isClosing = true;
    
    if (this.iframeMode !== IframeMode.NONE) {
      this._iframeModeService.sendMessageToParent('CLOSED', { status: 'closed' });
    }
    
    this.closeTimeout = window.setTimeout(() => {
      this.showModal = false;
      this.isClosing = false;
      this.closeTimeout = undefined;
    }, 300);
  }
}
